import React, { useState, useEffect, useMemo } from 'react'
import {
  ActionButton,
  ActionButtonIcons,
  Autocomplete,
  Button,
  Checkbox,
  DayTimeSelector,
  MultiselectCombobox,
  Select,
  formatDayTimeSelectorValue,
  parseDayTimeSelectorValue,
  type AutocompleteOption,
  type DayTimeSelectorValue,
} from '@enamelai/design-system'
import { Icon as AddIcon } from '@enamelai/design-system/icons/Add'
import './FormGrid.css'
import { Patient } from '@dataTypes/PatientInfo.types'
import {
  familyMemberOptions,
  contactViaOptions,
} from '@constants/PatientManagement/options'
import { searchPatientsByName } from '@services/api/patient.api'
import {
  getAllProviders,
  searchProvidersByName,
} from '@services/api/provider.api'
import { PatientWithInsurance } from '@dataTypes/appointment.type'
import { getFullName } from '@utils/fullname.util'
import { Provider } from '@dataTypes/provider.types'
import type {
  Allergy,
  MedicalCondition,
} from '@dataTypes/allergy-medical-condition.types'
import { useAppSelector } from '@store/hooks'
import { providerRoles } from '@utils/provider.util'
import { isHygienistWorkRole } from '@utils/provider-display.utils'

import { logger } from '@utils/safeLogger'

type BackgroundStepProps = {
  patientData: Patient
  handleInputChange: (
    field: keyof Patient,
    value: Patient[keyof Patient],
  ) => void
  validationErrors: { [key: string]: string }
  kioskMode?: boolean
  clinicId?: string
  accessToken?: string
  allergiesCatalog?: Allergy[]
  medicalConditionsCatalog?: MedicalCondition[]
}

const BackgroundStep: React.FC<BackgroundStepProps> = ({
  patientData,
  handleInputChange,
  kioskMode = false,
  clinicId,
  accessToken,
  allergiesCatalog: suppliedAllergiesCatalog,
  medicalConditionsCatalog: suppliedMedicalConditionsCatalog,
}) => {
  const [patients, setPatients] = useState<PatientWithInsurance[]>([])
  const [providers, setProviders] = useState<Provider[]>([])
  const [hygienists, setHygienists] = useState<any[]>([])
  const selectedClinic = useAppSelector((state) => state.clinic.selectedClinic)
  const storedAllergiesCatalog = useAppSelector(
    (state) => state.allergyMedicalCondition.allergies,
  )
  const storedMedicalConditionsCatalog = useAppSelector(
    (state) => state.allergyMedicalCondition.medicalConditions,
  )
  const allergiesCatalog = suppliedAllergiesCatalog ?? storedAllergiesCatalog
  const medicalConditionsCatalog =
    suppliedMedicalConditionsCatalog ?? storedMedicalConditionsCatalog
  const effectiveClinicId = clinicId ?? selectedClinic?.clinic_id
  const allergyOptions = useMemo(
    () =>
      allergiesCatalog.map((allergy) => ({
        value: allergy.allergy_id,
        label: allergy.title,
      })),
    [allergiesCatalog],
  )
  const conditionOptions = useMemo(
    () =>
      medicalConditionsCatalog.map((condition) => ({
        value: condition.medical_condition_id,
        label: condition.title,
      })),
    [medicalConditionsCatalog],
  )
  const [loading, setLoading] = useState(false)
  const [debouncedName, setDebouncedName] = useState('')
  const [debouncedProviderName, setDebouncedProviderName] = useState('')
  const [debouncedHygienistName, setDebouncedHygienistName] = useState('')
  const [familyMembers, setFamilyMembers] = useState<any[]>([
    {
      family_member_id: '',
      family_member_name: '',
      relation_to_patient: '',
    },
  ])
  const [familyMembersToEdit, setFamilyMembersToEdit] = useState<boolean>(false)
  const [familyMembersToEditIndex, setFamilyMembersToEditIndex] =
    useState<number>(0)
  const [appointmentTime, setAppointmentTime] = useState<DayTimeSelectorValue>(
    () => parseDayTimeSelectorValue(patientData.preferred_appointment_time),
  )

  useEffect(() => {
    if (!kioskMode || !effectiveClinicId) return
    let cancelled = false

    void getAllProviders(effectiveClinicId, { accessToken })
      .then((response) => {
        if (cancelled) return
        const providerList = response.data?.providers ?? []
        setProviders(
          providerList.filter(
            (provider) =>
              providerRoles.includes(provider.work_role ?? '') &&
              !isHygienistWorkRole(provider.work_role),
          ),
        )
        setHygienists(
          providerList.filter((provider) =>
            isHygienistWorkRole(provider.work_role),
          ),
        )
      })
      .catch(() => {
        if (cancelled) return
        setProviders([])
        setHygienists([])
      })

    return () => {
      cancelled = true
    }
  }, [accessToken, effectiveClinicId, kioskMode])

  const handleAppointmentTimeChange = (value: DayTimeSelectorValue): void => {
    setAppointmentTime(value)
    handleInputChange(
      'preferred_appointment_time',
      formatDayTimeSelectorValue(value),
    )
  }
  const handleCheckboxChange = (method: string, checked: boolean): void => {
    switch (method) {
      case 'Phone':
        handleInputChange('contact_via_phone', checked)
        break
      case 'SMS':
        handleInputChange('contact_via_sms', checked)
        break
      case 'Email':
        handleInputChange('contact_via_email', checked)
        break
      case 'App':
        handleInputChange('contact_via_app', checked)
        break
    }
  }

  const isPreferedContact = (method: string): boolean => {
    switch (method) {
      case 'Phone':
        return patientData.contact_via_phone
      case 'SMS':
        return patientData.contact_via_sms
      case 'Email':
        return patientData.contact_via_email
      case 'App':
        return patientData.contact_via_app
      default:
        return false
    }
  }

  useEffect(() => {
    if (kioskMode) return
    if (patientData.preferredProvider) {
      handleInputChange(
        'preferred_provider_name',
        `${patientData.preferredProvider.first_name} ${patientData.preferredProvider.last_name}`,
      )
    }
    if (patientData.preferredHygienist) {
      handleInputChange(
        'preferred_hygienist_name',
        `${patientData.preferredHygienist.first_name} ${patientData.preferredHygienist.last_name}`,
      )
    }

    if (patientData.relatives) {
      setFamilyMembers(patientData.relatives)
    } else {
      if (
        patientData.family_members_info &&
        patientData.family_members_info.length > 0
      ) {
        const relatives = patientData.family_members_info.map(
          (member: any) => ({
            family_member_id: member.patient_member_id,
            family_member_name: getFullName(member.patient_member_info, true),
            relation_to_patient: member.relation_to_patient,
          }),
        )
        setFamilyMembers(relatives)
      }
    }
  }, [])

  useEffect(() => {
    if (kioskMode) return
    handleInputChange('relatives', familyMembers)
  }, [familyMembers, kioskMode])

  const fetchPatients = async (name: string): Promise<void> => {
    try {
      setLoading(true)
      const existingMemberIds = familyMembers
        ?.map((member: any) => member.family_member_id)
        .join(',')
      const response = await searchPatientsByName(
        name,
        existingMemberIds || undefined,
      )
      if (response.statusCode === 200) {
        setPatients(response.data?.patients)
      } else {
        setPatients([])
      }
    } catch (error) {
      logger.error('Error fetching patients:', error)
      setPatients([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    const timer = setTimeout(() => {
      if (debouncedName.trim()) {
        fetchPatients(debouncedName.trim())
      } else {
        setPatients([])
      }
    }, 300) // Adjust debounce time as needed (e.g., 300ms)

    return () => clearTimeout(timer)
  }, [debouncedName])

  const patientNameHandleChange = (value: string, index: number): void => {
    setFamilyMembers(
      familyMembers.map((member, i) => {
        if (i === index) {
          return { ...member, family_member_id: '', family_member_name: value }
        }
        return member
      }),
    )
    setDebouncedName(value)
  }

  const relationToPatientHandleChange = (
    value: string,
    index: number,
  ): void => {
    setFamilyMembers(
      familyMembers.map((member, i) => {
        if (i === index) {
          return { ...member, relation_to_patient: value }
        }
        return member
      }),
    )
    setDebouncedName(value)
  }

  const handlePatientSelect = (
    patient: PatientWithInsurance,
    index: number,
  ): void => {
    setFamilyMembers(
      familyMembers.map((member, i) => {
        if (i === index) {
          return {
            ...member,
            family_member_name: getFullName(patient, true),
            family_member_id: patient.patient_id,
          }
        }
        return member
      }),
    )
    setPatients([])
  }

  const fetchProviders = async (name: string): Promise<void> => {
    try {
      setLoading(true)
      const response = await searchProvidersByName(name, effectiveClinicId, {
        accessToken,
      })
      if (response.statusCode === 200) {
        if ('providers' in response.data) {
          const providerList = Array.isArray(response.data?.providers)
            ? response.data.providers
            : []

          const filteredProvidersByClinic = providerList.filter(
            (prov) =>
              (!effectiveClinicId ||
                !prov.clinic_id ||
                prov.clinic_id === effectiveClinicId) &&
              providerRoles.includes(prov.work_role ?? '') &&
              !isHygienistWorkRole(prov.work_role),
          )

          setProviders(filteredProvidersByClinic as Provider[])
        } else {
          setProviders([])
        }
      } else {
        setProviders([])
      }
    } catch (error) {
      logger.error('Error fetching providers:', error)
      setProviders([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    const timer = setTimeout(() => {
      if (debouncedProviderName.trim()) {
        fetchProviders(debouncedProviderName.trim())
      } else if (!kioskMode) {
        setProviders([])
      }
    }, 300) // Adjust debounce time as needed (e.g., 300ms)

    return () => clearTimeout(timer)
  }, [debouncedProviderName])

  const providerNameHandleChange = (value: string): void => {
    handleInputChange('preferred_provider_name', value)
    handleInputChange('preferred_provider', null)
    setDebouncedProviderName(value)
  }

  const handleProviderSelect = (provider: any): void => {
    handleInputChange(
      'preferred_provider_name',
      `${provider.first_name} ${provider.last_name}`,
    )
    handleInputChange('preferred_provider', provider.provider_id)
    setProviders([])
  }

  const fetchHygienists = async (name: string): Promise<void> => {
    try {
      setLoading(true)
      const response = await searchProvidersByName(name, effectiveClinicId, {
        accessToken,
      })
      if (response.statusCode === 200) {
        if ('providers' in response.data) {
          const providerList = Array.isArray(response.data?.providers)
            ? response.data.providers
            : []

          const filteredProvidersByClinic = providerList.filter(
            (prov) =>
              (!effectiveClinicId ||
                !prov.clinic_id ||
                prov.clinic_id === effectiveClinicId) &&
              isHygienistWorkRole(prov.work_role),
          )

          setHygienists(filteredProvidersByClinic as Provider[])
        } else {
          setHygienists([])
        }
      } else {
        setHygienists([])
      }
    } catch (error) {
      logger.error('Error fetching hygienists:', error)
      setHygienists([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    const timer = setTimeout(() => {
      if (debouncedHygienistName.trim()) {
        fetchHygienists(debouncedHygienistName.trim())
      } else if (!kioskMode) {
        setHygienists([])
      }
    }, 300) // Adjust debounce time as needed (e.g., 300ms)

    return () => clearTimeout(timer)
  }, [debouncedHygienistName])

  const hygienistNameHandleChange = (value: string): void => {
    handleInputChange('preferred_hygienist_name', value)
    handleInputChange('preferred_hygienist', null)
    setDebouncedHygienistName(value)
  }

  const handleHygienistSelect = (hygienist: any): void => {
    handleInputChange(
      'preferred_hygienist_name',
      `${hygienist.first_name} ${hygienist.last_name}`,
    )
    handleInputChange('preferred_hygienist', hygienist.provider_id)
    setHygienists([])
  }

  const familyMemberOptionsFor = (index: number): AutocompleteOption[] =>
    familyMembersToEditIndex === index
      ? patients.map((patient) => ({
          value: patient.patient_id,
          label: getFullName(patient, true),
        }))
      : []

  const handleFamilyMemberChange = (value: string, index: number): void => {
    setFamilyMembersToEditIndex(index)
    const match = patients.find(
      (patient) => getFullName(patient, true) === value,
    )
    if (match) {
      handlePatientSelect(match, index)
    } else {
      patientNameHandleChange(value, index)
    }
  }

  const providerOptions: AutocompleteOption[] = providers.map((provider) => ({
    value: provider.provider_id,
    label: `${provider.first_name} ${provider.last_name}`,
  }))

  const hygienistOptions: AutocompleteOption[] = hygienists.map(
    (hygienist) => ({
      value: hygienist.provider_id,
      label: `${hygienist.first_name} ${hygienist.last_name}`,
    }),
  )

  const handleProviderChange = (value: string): void => {
    const match = providers.find(
      (provider) => `${provider.first_name} ${provider.last_name}` === value,
    )
    if (match) {
      handleProviderSelect(match)
    } else {
      providerNameHandleChange(value)
    }
  }

  const handleHygienistChange = (value: string): void => {
    const match = hygienists.find(
      (hygienist) => `${hygienist.first_name} ${hygienist.last_name}` === value,
    )
    if (match) {
      handleHygienistSelect(match)
    } else {
      hygienistNameHandleChange(value)
    }
  }

  const handleAddMoreFamilyMember = (): void => {
    setFamilyMembers([
      ...familyMembers,
      {
        family_member_id: '',
        family_member_name: '',
        relation_to_patient: '',
      },
    ])
  }

  const handleRemoveFamilyMember = (index: number): void => {
    if (familyMembers.length > 1) {
      setFamilyMembers(familyMembers.filter((_, i) => i !== index))
    } else {
      setFamilyMembers([
        {
          family_member_id: '',
          family_member_name: '',
          relation_to_patient: '',
        },
      ])
    }
  }
  return (
    <div className="add-patient-step">
      <div className="form-grid">
        <div className="form-grid__col--12">
          <MultiselectCombobox
            label="Allergies"
            placeholder="Select allergies"
            options={allergyOptions}
            value={patientData.allergy_ids ?? []}
            onChange={(value) => handleInputChange('allergy_ids', value)}
          />
        </div>
        <div className="form-grid__col--12">
          <MultiselectCombobox
            label="Medical conditions"
            placeholder="Select medical conditions"
            options={conditionOptions}
            value={patientData.medical_condition_ids ?? []}
            onChange={(value) =>
              handleInputChange('medical_condition_ids', value)
            }
          />
        </div>
      </div>

      {!kioskMode && (
        <div className="add-patient-family">
          <div className="add-patient-family__header">
            <span className="add-patient-family__title">Family members</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setFamilyMembersToEdit(!familyMembersToEdit)}
            >
              {familyMembersToEdit ? 'Done' : 'Edit'}
            </Button>
          </div>
          {familyMembers.map((member, index) => (
            <div className="add-patient-family__row" key={index}>
              <Autocomplete
                allowCustom
                label={index === 0 ? 'Name' : undefined}
                placeholder="Search family member"
                value={member.family_member_name || ''}
                options={familyMemberOptionsFor(index)}
                onChange={(value) => handleFamilyMemberChange(value, index)}
              />
              <Select
                label={index === 0 ? 'Relation to patient' : undefined}
                placeholder="Relation to patient"
                options={familyMemberOptions}
                value={member.relation_to_patient || ''}
                onChange={(value) =>
                  relationToPatientHandleChange(String(value), index)
                }
              />
              {familyMembersToEdit && (
                <ActionButton
                  icon={ActionButtonIcons.delete()}
                  label="Remove family member"
                  tone="danger"
                  onClick={() => handleRemoveFamilyMember(index)}
                />
              )}
            </div>
          ))}
          <div className="add-patient-family__add">
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<AddIcon aria-hidden="true" />}
              onClick={handleAddMoreFamilyMember}
            >
              Add more
            </Button>
          </div>
        </div>
      )}

      <div className="form-grid">
        <div className="form-grid__col--6">
          <DayTimeSelector
            label="Preferred appointment times"
            value={appointmentTime}
            onChange={handleAppointmentTimeChange}
          />
        </div>
        <div className="form-grid__col--6">
          <fieldset className="add-patient-contact">
            <legend className="add-patient-contact__label">Contact via</legend>
            <div className="add-patient-contact__options">
              {contactViaOptions.map((method) => (
                <Checkbox
                  key={method.value}
                  label={method.label}
                  checked={isPreferedContact(method.value)}
                  onChange={(e) =>
                    handleCheckboxChange(method.value, e.target.checked)
                  }
                />
              ))}
            </div>
          </fieldset>
        </div>
      </div>

      <div className="form-grid">
        <div className="form-grid__col--6">
          <Autocomplete
            allowCustom
            optional
            label="Preferred provider"
            placeholder="Search preferred provider"
            value={patientData.preferred_provider_name || ''}
            options={providerOptions}
            onChange={handleProviderChange}
          />
        </div>
        <div className="form-grid__col--6">
          <Autocomplete
            allowCustom
            optional
            label="Preferred hygienist"
            placeholder="Search preferred hygienist"
            value={patientData.preferred_hygienist_name || ''}
            options={hygienistOptions}
            onChange={handleHygienistChange}
          />
        </div>
      </div>
    </div>
  )
}

export default React.memo(BackgroundStep)
